"use client"

import type React from "react"
import { useState, useMemo, useRef, useEffect } from "react"
import { Card, CardBody, Button, FormGroup, Label, Alert, Spinner, Table, Badge } from "reactstrap"
import type { Exhibitor, VerifiedExhibitor } from "./types"
import { FaBuilding, FaChevronDown, FaSearch, FaCheckCircle, FaUsers, FaCar, FaArrowRight } from "react-icons/fa"

// Enhanced Verified Exhibitors List Component (Compact)
interface VerifiedExhibitorsListProps {
  verifiedExhibitors: VerifiedExhibitor[]
  onSelectExhibitor: (exhibitor: VerifiedExhibitor) => void
}

const VerifiedExhibitorsList: React.FC<VerifiedExhibitorsListProps> = ({ verifiedExhibitors, onSelectExhibitor }) => {
  if (verifiedExhibitors.length === 0) {
    return (
      <Card className="h-100 border-0 shadow-sm">
        <CardBody className="d-flex flex-column justify-content-center align-items-center text-center py-5">
          <div className="bg-light rounded-circle p-3 mb-3">
            <FaBuilding className="text-muted" size={20} />
          </div>
          <h6 className="fw-semibold">No Verified Companies</h6>
          <p className="text-muted small mb-0">
            Companies you verify will appear here for quick access.
          </p>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card className="h-100 border-0 shadow-sm">
      <CardBody className="p-3">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="fw-semibold mb-0">Recent Verified Companies</h6>
          <Badge color="success" pill className="px-2">
            {verifiedExhibitors.length}
          </Badge>
        </div>

        <div className="table-responsive">
          <Table borderless size="sm" className="mb-0">
            <thead>
              <tr>
                <th className="ps-0 py-2 fw-medium small text-muted" style={{fontSize: '0.8rem'}}>COMPANY</th>
                <th className="py-2 fw-medium small text-muted text-center" style={{fontSize: '0.8rem'}}>STATUS</th>
                <th className="pe-0 py-2 fw-medium small text-muted text-end" style={{fontSize: '0.8rem'}}>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {verifiedExhibitors.map((exhibitor) => (
                <tr 
                  key={exhibitor.id} 
                  className="cursor-pointer align-middle"
                  style={{ height: '52px' }}
                  onClick={() => onSelectExhibitor(exhibitor)}
                >
                  <td className="ps-0">
                    <div className="d-flex align-items-center">
                      <div className="bg-light p-2 rounded-circle me-2">
                        <FaBuilding className="text-primary" size={12} />
                      </div>
                      <div className="ms-1">
                        <div className="fw-medium small" style={{fontSize: '0.85rem'}}>{exhibitor.name}</div>
                        {exhibitor.email && (
                          <div className="text-muted" style={{fontSize: '0.75rem'}}>
                            {exhibitor.email.length > 20 
                              ? `${exhibitor.email.substring(0, 20)}...` 
                              : exhibitor.email
                            }
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="text-center">
                    <Badge color="success" pill className="px-2 py-1 d-inline-flex align-items-center" style={{fontSize: '0.7rem'}}>
                      <FaCheckCircle className="me-1" size={10} />
                      Verified
                    </Badge>
                  </td>
                  <td className="pe-0 text-end">
                    <Button
                      color="outline-primary"
                      size="sm"
                      className="px-2 py-1"
                      style={{fontSize: '0.75rem'}}
                      onClick={(e) => {
                        e.stopPropagation()
                        onSelectExhibitor(exhibitor)
                      }}
                    >
                      Manage
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        
        <div className="mt-3 pt-2 border-top text-center">
          <small className="text-muted">
            Click on any company to manage participants and cars
          </small>
        </div>
      </CardBody>
    </Card>
  )
}

export default VerifiedExhibitorsList
